import {useMessage} from '@coolcolduk/ui-mui';
import {Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, MoreVert as MoreVertIcon} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {JSX, useEffect, useRef, useState} from 'react';
import {CouponCode, CouponCodeInput, CouponCodeUpdateInput, CouponStatus} from '../../__generated__/resolvers-types';
import {useCreateCouponCode} from '../../hook/useCreateCouponCode';
import {useDeleteCouponCode} from '../../hook/useDeleteCouponCode';
import {useListCouponCodes} from '../../hook/useListCouponCodes';
import {useUpdateCouponCode} from '../../hook/useUpdateCouponCode';
import CouponCodeForm from './CouponCodeForm';

const ITEMS_PER_PAGE = 20;

export default function CouponCodeSettings(): JSX.Element {
  const [selectedCoupon, setSelectedCoupon] = useState<CouponCode | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCouponForMenu, setSelectedCouponForMenu] = useState<CouponCode | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {showMessage} = useMessage();
  const {
    data: couponCodes,
    loading,
    error,
    refetch,
    loadMore,
  } = useListCouponCodes({
    limit: ITEMS_PER_PAGE,
  });
  const createCouponCode = useCreateCouponCode();
  const updateCouponCode = useUpdateCouponCode();
  const deleteCouponCode = useDeleteCouponCode();

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !isLoadingMore) {
          const currentLength = couponCodes.length;
          setIsLoadingMore(true);

          loadMore(currentLength)
            .then(() => {
              // Check if new items were added
              setTimeout(() => {
                if (couponCodes.length === currentLength) {
                  setHasMore(false);
                }
                setIsLoadingMore(false);
              }, 100);
            })
            .catch(() => {
              setIsLoadingMore(false);
            });
        }
      },
      {threshold: 0.1},
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, isLoadingMore, couponCodes.length, loadMore]);

  const handleCreateNew = () => {
    setSelectedCoupon(null);
    setIsFormOpen(true);
  };

  const handleEdit = (coupon: CouponCode) => {
    setSelectedCoupon(coupon);
    setIsFormOpen(true);
  };

  const handleDelete = async (coupon: CouponCode) => {
    if (window.confirm(`Are you sure you want to delete coupon "${coupon.name}"?`)) {
      try {
        await deleteCouponCode(coupon.id);
        showMessage(`Coupon "${coupon.name}" deleted successfully`, undefined, 'success');
        refetch();
      } catch (deleteError) {
        console.error('Failed to delete coupon:', deleteError);
        showMessage(`Failed to delete coupon "${coupon.name}"`, undefined, 'error');
      }
    }
  };

  const handleFormSubmit = async (input: CouponCodeInput) => {
    try {
      if (selectedCoupon) {
        // Convert CouponCodeInput to CouponCodeUpdateInput for updates
        const updateInput: CouponCodeUpdateInput = {
          name: input.name,
          status: input.status,
          maxUses: input.maxUses,
          expiryDays: input.expiryDays,
          durationDays: input.durationDays,
          couponRole: input.couponRole,
        };
        await updateCouponCode(selectedCoupon.id, updateInput);
        showMessage(`Coupon "${input.name}" updated successfully`, undefined, 'success');
      } else {
        await createCouponCode(input);
        showMessage(`Coupon "${input.name}" created successfully`, undefined, 'success');
      }
      setIsFormOpen(false);
      refetch();
    } catch (submitError) {
      console.error('Failed to save coupon:', submitError);
      const action = selectedCoupon ? 'update' : 'create';
      showMessage(`Failed to ${action} coupon`, undefined, 'error');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, coupon: CouponCode) => {
    setAnchorEl(event.currentTarget);
    setSelectedCouponForMenu(coupon);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCouponForMenu(null);
  };

  const getStatusColor = (status: CouponStatus) => {
    switch (status) {
      case CouponStatus.ACTIVE:
        return 'success';
      case CouponStatus.DISABLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const isExpired = (expiryDate: string) => dayjs(expiryDate).isBefore(dayjs());

  if (error) {
    return (
      <Alert severity="error" sx={{m: 2}}>
        Error loading coupon codes: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{p: 3}}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 3}}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew} disabled={loading}>
          Create Coupon
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Uses</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {couponCodes.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 'bold',
                          color: isExpired(coupon.expiryDate) ? 'error.main' : 'inherit',
                        }}
                      >
                        {coupon.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{coupon.name}</TableCell>
                    <TableCell>
                      <Chip label={coupon.status} color={getStatusColor(coupon.status) as any} size="small" />
                    </TableCell>
                    <TableCell>
                      {coupon.usedCount}
                      {coupon.maxUses && ` / ${coupon.maxUses}`}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={isExpired(coupon.expiryDate) ? 'error.main' : 'inherit'}>
                        {dayjs(coupon.expiryDate).format('MMM DD, YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={coupon.couponRole} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => handleMenuOpen(e, coupon)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Loading indicator for infinite scroll */}
          {hasMore && (
            <Box ref={loadingRef} sx={{display: 'flex', justifyContent: 'center', p: 2}}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Loading more indicator */}
          {isLoadingMore && (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ml: 1, alignSelf: 'center'}}>
                Loading more...
              </Typography>
            </Box>
          )}

          {/* End of list indicator */}
          {!hasMore && couponCodes.length > 0 && (
            <Box sx={{textAlign: 'center', p: 2, color: 'text.secondary'}}>
              <Typography variant="body2">No more coupon codes to load</Typography>
            </Box>
          )}

          {/* Empty state */}
          {!loading && couponCodes.length === 0 && (
            <Box sx={{textAlign: 'center', p: 4, color: 'text.secondary'}}>
              <Typography variant="body1">No coupon codes found</Typography>
              <Typography variant="body2" sx={{mt: 1}}>
                Create your first coupon code to get started
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            if (selectedCouponForMenu) {
              handleEdit(selectedCouponForMenu);
            }
            handleMenuClose();
          }}
        >
          <EditIcon sx={{mr: 1}} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedCouponForMenu) {
              handleDelete(selectedCouponForMenu);
            }
            handleMenuClose();
          }}
          sx={{color: 'error.main'}}
        >
          <DeleteIcon sx={{mr: 1}} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedCoupon ? 'Edit Coupon Code' : 'Create New Coupon Code'}</DialogTitle>
        <DialogContent>
          <CouponCodeForm coupon={selectedCoupon} onSubmit={handleFormSubmit} onCancel={() => setIsFormOpen(false)} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
